import {
    alpha,
    MenuItem,
    TextField,
    TextFieldProps,
    InputAdornment,
    Box,
    Select,
    FormControl,
    styled,
    inputBaseClasses,
    outlinedInputClasses,
    CircularProgress,
} from '@mui/material';
import { generateNamesObject } from '@/utils/stringUtils';
import { Field, FieldProps, useFormikContext } from 'formik';
import {
    parsePhoneNumberFromString,
    getCountryCallingCode,
    AsYouType,
    CountryCode,
    parsePhoneNumber,
} from 'libphonenumber-js';
import { get } from 'lodash';
import React, { useState } from 'react';
import { FieldConfigProps } from '../types';
import { CountryOptionType } from './types';
import { CountryOption } from './CountryOption';

export const formPhoneNumberFieldClassnames = generateNamesObject(['code', 'phoneNumber'], 'FormPhoneNumberField');

const Container = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    [`.${inputBaseClasses.input}`]: {
        color: theme.palette.text.primary,
        borderColor: theme.palette.text.secondary,
    },
    [`.${outlinedInputClasses.notchedOutline}`]: {
        borderColor: alpha(theme.palette.text.secondary, 0.3),
    },
    [`.${outlinedInputClasses.notchedOutline}`]: {
        borderColor: alpha(theme.palette.text.secondary, 0.3),
    },
    [`.${inputBaseClasses.root}:not(.Mui-focused):not(.Mui-disabled):hover .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.text.secondary,
    },
}));

type PhoneNumberFieldProps = FieldConfigProps &
    Partial<TextFieldProps> & {
        name: string;
        countries: CountryOptionType[];
        isLoadingCountries?: boolean;
    };

const validatePhoneNumber = (value: string, selectedCountry: CountryCode) => {
    const isValid = parsePhoneNumberFromString(value, selectedCountry)?.isValid();

    if (!isValid) {
        return 'Invalid phone number';
    }

    return undefined;
};

const parseInitialPhoneNumberValue = (value: string) => {
    try {
        return parsePhoneNumber(value);
    } catch {
        return {
            countryCallingCode: '',
            nationalNumber: '',
            country: 'US' as CountryCode,
        };
    }
};

export const FormPhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
    countries,
    name,
    label,
    size = 'medium',
    disabled,
    isLoadingCountries = false,
    ...rest
}) => {
    const { values } = useFormikContext();
    const fieldValue = get(values, name);
    const initialParsed = parseInitialPhoneNumberValue(fieldValue);
    const initialCountry = initialParsed.country || 'US';

    const [phoneNumber, setPhoneNumber] = useState<string>(initialParsed.nationalNumber);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(initialCountry);

    return (
        <Field name={name} validate={(value: string) => validatePhoneNumber(value, selectedCountry)}>
            {({ field, form: { setFieldValue, touched, errors } }: FieldProps) => {
                const error = get(touched, field.name) && (get(errors, field.name) as string);
                const callingCode = getCountryCallingCode(selectedCountry);

                const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const rawValue = e.target.value;
                    const onlyDigits = rawValue.replace(/\D/g, '');
                    const formatter = new AsYouType(selectedCountry);
                    const formatted = formatter.input(onlyDigits);

                    setPhoneNumber(formatted);
                    setFieldValue(name, `+${callingCode}${onlyDigits}`);
                };

                return (
                    <Container>
                        <FormControl size={size} className={formPhoneNumberFieldClassnames.code}>
                            <Select
                                value={selectedCountry}
                                size={size}
                                disabled={disabled}
                                onChange={(e) => {
                                    const countryCode = e.target.value as CountryCode;
                                    const callingCode = getCountryCallingCode(countryCode);
                                    setSelectedCountry(countryCode);
                                    setPhoneNumber('');
                                    setFieldValue(name, `+${callingCode}`);
                                }}
                                MenuProps={{
                                    sx: { maxHeight: 400 },
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                }}
                                renderValue={(value) => {
                                    const selected = countries.find((c) => c.code === value);

                                    if (!selected) {
                                        return value;
                                    }

                                    return (
                                        <Box display="flex" alignItems="center" mr={2}>
                                            {isLoadingCountries ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <CountryOption {...selected} withTextLabel={false} />
                                            )}
                                        </Box>
                                    );
                                }}
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>
                                        <CountryOption {...country} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            {...field}
                            value={phoneNumber}
                            label={label || 'Phone number'}
                            onChange={handlePhoneChange}
                            size={size}
                            fullWidth
                            error={Boolean(error)}
                            helperText={error ?? rest.helperText}
                            disabled={disabled}
                            inputProps={{
                                inputMode: 'numeric',
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">+{callingCode}</InputAdornment>,
                                error: Boolean(error),
                            }}
                            className={formPhoneNumberFieldClassnames.phoneNumber}
                        />
                    </Container>
                );
            }}
        </Field>
    );
};
